import React, { useState } from 'react';
import { Button } from '@/components/Button';
import { UserRole, getUserRoleString, type User } from '@/types/api';
import { userService } from '@/services/userService';
import { exportService } from '@/services/exportService';

interface BulkOperationsProps {
  selectedUsers: User[];
  allUsers: User[];
  onSelectionChange: (userIds: string[]) => void;
  onBulkActionComplete: (action: string, count: number) => void;
  onError: (error: string) => void;
  isUsingMockData: boolean;
}

type BulkAction = 'activate' | 'deactivate' | 'delete' | 'export' | 'changeRole';

/**
 * Bulk Operations Component
 * 
 * Provides bulk selection and operations for user management,
 * including activate, deactivate, delete, export, and role changes.
 */
export function BulkOperations({
  selectedUsers,
  allUsers,
  onSelectionChange,
  onBulkActionComplete,
  onError,
  isUsingMockData
}: BulkOperationsProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [activeAction, setActiveAction] = useState<BulkAction | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState<{
    action: BulkAction;
    title: string;
    message: string;
    data?: any;
  } | null>(null);
  const [newRole, setNewRole] = useState<UserRole>(UserRole.Agent);
  const [showRoleChangeDialog, setShowRoleChangeDialog] = useState(false);

  const selectedUserIds = selectedUsers.map(user => user.id);
  const isAllSelected = allUsers.length > 0 && selectedUserIds.length === allUsers.length;
  const isPartiallySelected = selectedUserIds.length > 0 && selectedUserIds.length < allUsers.length;

  // Handle select all/none
  const handleSelectAll = () => {
    if (isAllSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(allUsers.map(user => user.id));
    }
  };

  // Handle individual selection
  const handleUserSelect = (userId: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedUserIds, userId]);
    } else {
      onSelectionChange(selectedUserIds.filter(id => id !== userId));
    }
  };

  // Export users to CSV with proper formatting
  const exportToCSV = (users: User[]) => {
    const headers = [
      'Name',
      'Email',
      'Role',
      'Company',
      'Status',
      'Created Date',
      'Last Login',
      'Department',
      'Job Title',
      'Phone'
    ];

    // Helper function to escape CSV values
    const escapeCsvValue = (value: string) => {
      if (!value) return '';
      if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const csvContent = [
      headers.join(','),
      ...users.map(user => {
        const name = `${user.firstName} ${user.lastName}`.trim();
        const email = user.email || '';
        const roleText = user.role;
        const company = user.company?.name || '';
        const status = user.isActive ? 'Active' : 'Inactive';
        const createdDate = new Date(user.createdAt).toLocaleDateString();
        const lastLogin = user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never';
        const department = user.department || '';
        const jobTitle = user.jobTitle || '';
        const phone = user.phoneNumber || '';

        return [
          escapeCsvValue(name),
          escapeCsvValue(email),
          escapeCsvValue(roleText),
          escapeCsvValue(company),
          escapeCsvValue(status),
          createdDate,
          lastLogin,
          escapeCsvValue(department),
          escapeCsvValue(jobTitle),
          escapeCsvValue(phone)
        ].join(',');
      })
    ].join('\n');

    // Add UTF-8 BOM for Excel compatibility
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `selected-users-export-${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Cleanup
    URL.revokeObjectURL(url);
  };

  // Handle bulk action confirmation
  const handleBulkAction = async (action: BulkAction, data?: any) => {
    const count = selectedUsers.length;
    
    if (count === 0) {
      onError('Please select at least one user.');
      return;
    }

    let title = '';
    let message = '';

    switch (action) {
      case 'activate':
        title = 'Activate Users';
        if (count === 1) {
          const userName = `${selectedUsers[0].firstName} ${selectedUsers[0].lastName}`.trim();
          message = `Are you sure you want to activate ${userName}?`;
        } else {
          message = `Are you sure you want to activate ${count} users?`;
        }
        break;
      case 'deactivate':
        title = 'Deactivate Users';
        if (count === 1) {
          const userName = `${selectedUsers[0].firstName} ${selectedUsers[0].lastName}`.trim();
          message = `Are you sure you want to deactivate ${userName}? They will not be able to log in.`;
        } else {
          message = `Are you sure you want to deactivate ${count} users? They will not be able to log in.`;
        }
        break;
      case 'delete':
        title = 'Delete Users';
        if (count === 1) {
          const userName = `${selectedUsers[0].firstName} ${selectedUsers[0].lastName}`.trim();
          message = `Are you sure you want to delete ${userName}? This action cannot be undone.`;
        } else {
          message = `Are you sure you want to delete ${count} users? This action cannot be undone.`;
        }
        break;
      case 'changeRole':
        title = 'Change User Roles';
        // Convert enum to user-friendly role name
        const getRoleDisplayName = (role: UserRole) => {
          switch (role) {
            case UserRole.Admin: return 'Administrator';
            case UserRole.Agent: return 'Support Agent';
            case UserRole.Manager: return 'Manager';
            case UserRole.EndUser: return 'End User';
            default: return getUserRoleString(role);
          }
        };

        // Show user name when only 1 user selected, otherwise show count
        if (count === 1) {
          const userName = `${selectedUsers[0].firstName} ${selectedUsers[0].lastName}`.trim();
          message = `Are you sure you want to change the role of ${userName} to ${getRoleDisplayName(data.role)}?`;
        } else {
          message = `Are you sure you want to change the role of ${count} users to ${getRoleDisplayName(data.role)}?`;
        }
        break;
      case 'export':
        // Export doesn't need confirmation - use the centralized export service
        try {
          await exportService.exportUsers({
            // Since we're exporting selected users, we could filter by their IDs
            // For now, we'll export all users that match the current selection criteria
          });
          onBulkActionComplete('export', count);
        } catch (error) {
          onError('Failed to export users');
        }
        return;
    }

    setShowConfirmDialog({
      action,
      title,
      message,
      data
    });
  };

  // Execute confirmed bulk action
  const executeBulkAction = async () => {
    if (!showConfirmDialog) return;

    const { action, data } = showConfirmDialog;
    const count = selectedUsers.length;

    if (isUsingMockData) {
      // Demo mode - just show success message
      setShowConfirmDialog(null);
      onSelectionChange([]);
      onBulkActionComplete(action, count);
      return;
    }

    try {
      setIsLoading(true);
      setActiveAction(action);

      switch (action) {
        case 'activate':
          await Promise.all(
            selectedUserIds.map(userId => userService.activateUser(userId))
          );
          break;
        
        case 'deactivate':
          await userService.bulkDeactivateUsers(selectedUserIds);
          break;
        
        case 'delete':
          await Promise.all(
            selectedUserIds.map(userId => userService.deleteUser(userId))
          );
          break;
        
        case 'changeRole':
          await userService.bulkUpdateUsers(selectedUserIds, { role: data.role });
          break;
      }

      setShowConfirmDialog(null);
      onSelectionChange([]);
      onBulkActionComplete(action, count);

    } catch (error: any) {
      console.error(`Bulk ${action} failed:`, error);
      onError(`Failed to ${action} users. Please try again.`);
    } finally {
      setIsLoading(false);
      setActiveAction(null);
    }
  };

  return (
    <>
      {/* Floating Contextual Toolbar - appears near selections */}
      {selectedUsers.length > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40 animate-in slide-in-from-bottom-2 duration-200">
          <div className="bg-white shadow-lg border border-gray-200 rounded-lg p-4 max-w-4xl">
            <div className="flex items-center justify-center space-x-3">
              {/* Selection Info */}
              <div className="flex items-center mr-4">
                <svg className="h-5 w-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium text-gray-900">
                  {selectedUsers.length} selected
                </span>
              </div>

              {/* Primary Actions */}
              <div className="flex items-center space-x-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('activate')}
                  disabled={isLoading || selectedUsers.every(u => u.isActive)}
                  className="h-[32px] text-xs"
                >
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Activate
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('deactivate')}
                  disabled={isLoading || selectedUsers.every(u => !u.isActive)}
                  className="h-[32px] text-xs"
                >
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                  </svg>
                  Deactivate
                </Button>

                {/* Role Change - Modal-based approach for better UX */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRoleChangeDialog(true)}
                  disabled={isLoading}
                  className="h-[32px] text-xs"
                >
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  Change Role
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('export')}
                  disabled={isLoading}
                  className="h-[32px] text-xs"
                >
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('delete')}
                  disabled={isLoading}
                  className="h-[32px] text-xs text-red-600 border-red-300 hover:bg-red-50"
                >
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSelectionChange([])}
                  className="h-[32px] text-xs ml-2"
                >
                  ✕ Clear
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Select All Checkbox (for table header) */}
      <div className="flex items-center">
        <input
          type="checkbox"
          checked={isAllSelected}
          ref={input => {
            if (input) input.indeterminate = isPartiallySelected && !isAllSelected;
          }}
          onChange={handleSelectAll}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label className="sr-only">
          {isAllSelected ? 'Deselect all users' : 'Select all users'}
        </label>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                  showConfirmDialog.action === 'delete' ? 'bg-red-100' : 'bg-blue-100'
                }`}>
                  {showConfirmDialog.action === 'delete' ? (
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                  ) : (
                    <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                  )}
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <h3 className="text-base font-semibold leading-6 text-gray-900">
                    {showConfirmDialog.title}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      {showConfirmDialog.message}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <Button
                  variant={showConfirmDialog.action === 'delete' ? 'danger' : 'primary'}
                  onClick={executeBulkAction}
                  loading={isLoading}
                  className="w-full sm:ml-3 sm:w-auto"
                >
                  {showConfirmDialog.action === 'delete' ? 'Delete' : 'Confirm'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowConfirmDialog(null)}
                  disabled={isLoading}
                  className="mt-3 w-full sm:mt-0 sm:w-auto"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role Change Modal - Professional UI with clear intent */}
      {showRoleChangeDialog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                 onClick={() => setShowRoleChangeDialog(false)} />

            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
              <div className="text-center">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 mb-4">
                  <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>

                <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-2">
                  Change User Roles
                </h3>

                <div className="mt-2 text-sm text-gray-600 mb-6">
                  {selectedUsers.length === 1 ? (
                    <p>Change the role for <strong>{selectedUsers[0].fullName}</strong></p>
                  ) : (
                    <p>Change roles for <strong>{selectedUsers.length}</strong> selected users</p>
                  )}
                </div>

                {/* Role Selection */}
                <div className="mb-6">
                  <label htmlFor="role-select" className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Select New Role
                  </label>
                  <select
                    id="role-select"
                    value={newRole}
                    onChange={(e) => setNewRole(parseInt(e.target.value) as UserRole)}
                    className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                  >
                    <option value={UserRole.Admin}>Administrator</option>
                    <option value={UserRole.Agent}>Support Agent</option>
                    <option value={UserRole.EndUser}>End User</option>
                  </select>

                  {/* Role Description */}
                  <div className="mt-2 text-xs text-gray-500 text-left">
                    {newRole === UserRole.Admin && "Full system access with user management capabilities"}
                    {newRole === UserRole.Agent && "Can manage and respond to support tickets"}
                    {newRole === UserRole.EndUser && "Can create and view own support tickets"}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3">
                  <Button
                    variant="primary"
                    onClick={() => {
                      setShowRoleChangeDialog(false);
                      handleBulkAction('changeRole', { role: newRole });
                    }}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    {isLoading ? (
                      <>
                        <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Changing Roles...
                      </>
                    ) : (
                      <>
                        Change to {newRole === UserRole.Admin ? 'Administrator' : newRole === UserRole.Agent ? 'Support Agent' : 'End User'}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowRoleChangeDialog(false)}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Individual User Selection Checkbox Component
 * To be used in the user table rows
 */
interface UserSelectionCheckboxProps {
  user: User;
  isSelected: boolean;
  onSelectionChange: (userId: string, checked: boolean) => void;
}

export function UserSelectionCheckbox({ user, isSelected, onSelectionChange }: UserSelectionCheckboxProps) {
  return (
    <input
      type="checkbox"
      checked={isSelected}
      onChange={(e) => onSelectionChange(user.id, e.target.checked)}
      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
      aria-label={`Select ${user.fullName}`}
    />
  );
}